import React, { Component, useCallback } from "react";
import { useDialog } from "../../context/messaging-layer";
import Toasts from "../toast-notification";
import InfoIcon from "../../styles/default/assets/icons/c-info.svg";
import PPIcon from "../../styles/default/assets/icons/price-procedure.svg";
import { createPriceProceduresFetcher } from "../../api/price-procedures";

const PriceProcedure = ({ item, components, onProcedureClick }) => {
  const handleClick = () => {
    onProcedureClick(item);
  };
  return (
    <components.Text
      className='price-procedure'
      label={`${item.name} - ${item.code}`}
      onClick={handleClick}
    />
  );
};

const PopularList = ({ items, components, onProcedureClick }) => (
  <div className='price-procedures-popular-web-sdk'>
    {items.length ? (
      <components.Label text='Most popular cash procedures' />
    ) : null}
    {items.map(item => {
      return (
        <PriceProcedure
          components={components}
          item={item}
          key={`price-procedure-${item.code}`}
          onProcedureClick={onProcedureClick}
        />
      );
    })}
  </div>
);

const ProcedureDetails = ({
  components: { Text, Tooltip, Icon },
  item: { name, code, cash = [], insurance = [] }
}) => {
  const section1Text = `Your actual out-of-pocket costs may vary based on factors specific to your provider and/or your
              plan. Some plans base their reimbursement rates on a percentage of "usual, customary, and
              reasonable" charges, which is referred to as "UCR" Others use a formula based on the
              Medicare fee schedule that is published by the US Department of Health and Human Services. To learn
              how your health plan determines out-of-network reimbursement rates and covered services, call the
              number listed on the back of your insurance card.`;
  const section2Text = `HealthiestYou does not determine, develop, or establish any fee, reimbursement level or UCR for
              any procedure or service. Your plan is responsible for setting your actual reimbursement level, or UCR.
              Our data reflect benchmark market rates, which are what providers typically charge for a procedure
              or service in a particular geographic area. Estimates provided in this app are for informational
              purposes only.`;
  const { showDialog } = useDialog();
  const showDisclaimer = useCallback(() => {
    showDialog({
      actions: [{ label: "Close" }],
      content: {
        title: "Disclaimer",
        children: (
          <>
            <Text label={section1Text} />
            <Text label={section2Text} />
          </>
        )
      },
      headerClass: "price-procedure-dialog-header-web-sdk",
      footerClass: "price-procedure-dialog-footer-web-sdk",
      hyCloseButton: true
    });
  }, [showDialog]);
  return (
    <div className='price-procedure-details-web-sdk'>
      <Text
        label={`${name ? name + " - " : ""}${code}`}
        params='price-procedure-header-web-sdk'
      />
      <div className='price-item-wrapper-web-sdk'>
        <Text label='Avg cash pricing' params='avg-price-web-sdk' />
        <Tooltip message='These are the low, average and high prices you would expect to pay in your area if you were paying cash for a procedure rather than submitting to your insurance.'>
          <Icon showIcon={true} src={InfoIcon} />
        </Tooltip>
        <Text label={`$${cash[0]?.average}`} params='procedure-price-web-sdk' />
      </div>
      <div className='price-item-wrapper-web-sdk'>
        <Text
          label='Avg insurance pricing submitted'
          params='avg-price-web-sdk'
        />
        <Tooltip message='These are the low, average and high amounts submitted by patients to their insurance carriers in your area for this procedure.'>
          <Icon showIcon={true} src={InfoIcon} />
        </Tooltip>
        <Text
          label={`$${
            insurance.filter(item => item.type === "submitted")[0]?.average
          }`}
          params='procedure-price-web-sdk'
        />
      </div>
      <div className='price-item-wrapper-web-sdk'>
        <Text label='Avg insurance pricing paid' params='avg-price-web-sdk' />
        <Tooltip message='These are the low, average and high claims paid by insurance carriers in your area for this procedure.'>
          <Icon showIcon={true} src={InfoIcon} />
        </Tooltip>
        <Text
          label={`$${
            insurance.filter(item => item.type === "allowed")[0]?.average
          }`}
          params='procedure-price-web-sdk'
        />
      </div>
      <Text
        label='Disclaimer'
        onClick={showDisclaimer}
        params='procedure-disclaimer-web-sdk'
      />
    </div>
  );
};

const TOAST_ID = "__PRICE_PROCEDURES__";

class PriceProcedures extends Component {
  constructor(props) {
    super(props);
    this.handleChange = this.handleChange.bind(this);
    this.fetchProcedureDetails = this.fetchProcedureDetails.bind(this);
    this.handleEnterDown = this.handleEnterDown.bind(this);
    this.fetchPricesPromise = this.fetchPricesPromise.bind(this);
    this.handleZipCodeChange = this.handleZipCodeChange.bind(this);
    this.showToast = this.showToast.bind(this);
    this.state = {
      loading: false,
      zipCode: props.config?.zipCode || "",
      popular: [],
      selected: null
    };

    this.formFetcher = createPriceProceduresFetcher();
  }

  componentDidMount() {
    if (this.props.config?.procedureName) {
      this.fetchProcedureDetailsByName(this.props.config.procedureName);
    } else {
      this.fetchPopular();
    }
  }

  fetchProcedureDetailsByName(procedureName) {
    this.fetchPricesPromise(procedureName)
      .then(result => {
        if (result.length > 0) {
          this.fetchProcedureDetails(result[0].value);
        } else {
          this.fetchPopular();
        }
      })
      .catch(error => {
        this.showToast(error);
      });
  }

  fetchPopular() {
    this.setState(
      {
        loading: true
      },
      () => {
        this.formFetcher("pricing/procedures/cash/popular?type=cash").then(
          res => {
            this.setState({
              popular: res.data.popular,
              loading: false
            });
          }
        );
      }
    );
  }

  fetchProcedureDetails(selected) {
    if (!this.isCorrectZipCode(this.state.zipCode)) {
      this.showToast("Zip code is required");
      return;
    }
    if (!selected?.code) {
      return;
    }
    const searchParams = new URLSearchParams({
      zip: this.state.zipCode,
      cpt: selected.code || selected.cpt
    });
    this.setState(
      {
        loading: true
      },
      () => {
        this.formFetcher(`procedure/details?${searchParams}`).then(res => {
          this.setState(
            {
              selected: {
                ...selected,
                ...res.data
              },
              loading: false
            },
            () => {
              if (this.state.popular.length === 0) {
                this.fetchPopular();
              }
            }
          );
        });
      }
    );
  }

  fetchPricesPromise(userInput) {
    return new Promise(resolve => {
      if (this.isCptCodeEntered(userInput)) {
        resolve([]);
      } else {
        this.setState(
          {
            selected: null
          },
          () => {
            this.formFetcher(`procedure/search?name=${userInput}`).then(res => {
              if (res) {
                resolve(
                  (res || []).map(item => ({
                    label: `${item.description} - ${item.code}`,
                    value: {
                      name: item.description,
                      code: item.code
                    }
                  }))
                );
              }
            });
          }
        );
      }
    });
  }

  isCptCodeEntered(userInput) {
    return /^\d+$/.test(userInput);
  }

  handleChange(selected) {
    this.fetchProcedureDetails(selected);
  }

  handleEnterDown(e, entered) {
    e?.preventDefault();
    if (entered === "") {
      this.setState({
        selected: null
      });
    } else if (this.isCptCodeEntered(entered)) {
      this.fetchProcedureDetails({
        code: entered
      });
    }
  }

  handleZipCodeChange({ currentTarget: { value } }) {
    this.setState(
      {
        zipCode: value
      },
      () => {
        if (this.isCorrectZipCode(value)) {
          this.fetchProcedureDetails({ ...this.state.selected });
        }
      }
    );
  }

  isCorrectZipCode(zipCode) {
    return zipCode.length === 5;
  }

  showToast(message = "message") {
    this.props.addToast({
      id: TOAST_ID,
      message
    });
  }

  render() {
    const {
      components: { Title, Text, Autocomplete, Input, Icon, Loader },
      toasts,
      removeToast
    } = this.props;
    return (
      <div>
        <Toasts
          toasts={toasts}
          onClose={toastId => removeToast(toastId)}
          components={this.props.components}
        />
        <Title title='Price procedures' />
        <div className='price-procedure-content-wrapper-web-sdk'>
          <span className='price-procedure-icon-wrapper-web-sdk'>
            <Icon src={PPIcon} showIcon={true} />
          </span>
          <div>
            <Text label='To find pricing on different procedures, please enter the name of the procedures or the CPT Code above' />
            <div className='price-procedure-search-input-web-sdk'>
              <Autocomplete
                suggestions={
                  this.state.selected
                    ? [
                        {
                          ...this.state.selected,
                          value: this.state.selected.code,
                          label: this.state.selected.name
                        }
                      ]
                    : []
                }
                asyncFetch={true}
                showMagnifyingGlassIcon={true}
                showCrossIcon={true}
                asyncFetchPromise={this.fetchPricesPromise}
                minUserInputLength={3}
                handleChange={this.handleChange}
                onEnterDown={this.handleEnterDown}
                defaultValue={this.state.selected?.name}
              />
              <Input
                onChange={this.handleZipCodeChange}
                placeholder='Enter zip'
                name='zip'
                value={this.state.zipCode}
                params='price-procedure-zip-web-sdk'
              />
            </div>
            {!this.state.loading && this.state.selected ? (
              <ProcedureDetails
                components={this.props.components}
                item={this.state.selected}
              />
            ) : null}
            {!this.state.loading && !this.state.selected ? (
              <PopularList
                components={this.props.components}
                items={this.state.popular}
                onProcedureClick={this.fetchProcedureDetails}
              />
            ) : null}
          </div>
        </div>
        {this.state.loading ? <Loader /> : null}
      </div>
    );
  }
}

export default PriceProcedures;
